import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import type { Control, ControlSelections } from '../../types/controls';
import { type AppliedDashboardFilters, markApplied, markPrefill, replaceFilterParams } from '../applied/filterParams';
import type { TemplateControls } from '../templateControls';
import { withDependentsCleared } from './controlCascade';

type EditFilter = {
  setControlValueAndResetDependents: (key: string, values: string[]) => void;
  stageTimeRange: (value: string) => void;
  applyStagedFilters: () => void;
  applyControlValueNow: (key: string, values: string[]) => void;
};

/** No separate staging store: a change goes straight into the URL, marked so it is not rendered. */
export const useEditFilter = ({
  controls,
  template,
  applied,
  onApplied,
}: {
  controls: Control[];
  template: TemplateControls;
  applied: AppliedDashboardFilters;
  onApplied: (selections: ControlSelections, timeRange: string | undefined) => void;
}): EditFilter => {
  const [, setSearchParams] = useSearchParams();
  const { selectionsByControlKey, timeRangeOverride } = applied;

  const write = useCallback(
    (selections: ControlSelections, timeRange: string | undefined, mark: typeof markApplied) =>
      setSearchParams((prev) => mark(replaceFilterParams(new URLSearchParams(prev), selections, timeRange, template))),
    [setSearchParams, template],
  );

  const commit = useCallback(
    (selections: ControlSelections, timeRange: string | undefined) => {
      onApplied(selections, timeRange);
      write(selections, timeRange, markApplied);
    },
    [write, onApplied],
  );

  const withControlValue = useCallback(
    (controlKey: string, values: string[]) =>
      withDependentsCleared({ ...selectionsByControlKey, [controlKey]: values }, controlKey, controls),
    [selectionsByControlKey, controls],
  );

  return {
    setControlValueAndResetDependents: useCallback(
      (controlKey: string, values: string[]) =>
        write(withControlValue(controlKey, values), timeRangeOverride, markPrefill),
      [write, withControlValue, timeRangeOverride],
    ),
    stageTimeRange: useCallback(
      (value: string) => write(selectionsByControlKey, value, markPrefill),
      [write, selectionsByControlKey],
    ),
    applyStagedFilters: useCallback(
      () => commit(selectionsByControlKey, timeRangeOverride),
      [commit, selectionsByControlKey, timeRangeOverride],
    ),
    applyControlValueNow: useCallback(
      (controlKey: string, values: string[]) => commit(withControlValue(controlKey, values), timeRangeOverride),
      [commit, withControlValue, timeRangeOverride],
    ),
  };
};
